import { Injectable, NgZone } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  from,
  throwError,
} from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';

import { ProductAdsService, BulkExecutePayload } from 'src/app/core/services/product-ads.service';
import { ModalPreferencesService } from 'src/app/core/services/modal-preferences.service';
import { ManagedScope, ProductAds, ProductAdState } from 'src/app/core/models/product-ads';
import { CampaignProductAd } from 'src/app/core/models/campaign-product-ad';
import { SnotifyService } from 'ng-snotify';
import { MODAL_PREFERENCES_KEYS } from 'src/app/core/constants/modal-preferences';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { PusherService } from 'src/app/core/services/pusher.service';

const BULK_UPDATE_ADS_STATUS_KEY = 'bulkUpdateAdsStatus';

import { GeneralWarningModalComponent } from 'src/app/theme/shared/forecastui/general-warning-modal/general-warning-modal.component';
import { CompareChangesComponent } from 'src/app/theme/shared/forecastui/comparison-changes/comparison-changes.component';
import { environment } from 'src/environments/environment';

// Constants for tree hierarchy levels
const PARENT_LEVEL = 0;
const CHILD_LEVEL = 1;

// Type definitions
type TreeLevel = 0 | 1;

export enum ProductAdActionType {
  STATE_CHANGE = 'STATE_CHANGE',
  AUTO_MODE = 'AUTO_MODE',
  ACTIVE_STATUS = 'ACTIVE_STATUS',
  MANAGED_FLAG = 'MANAGED_FLAG',
  EXTERNAL_MUTATION = 'EXTERNAL_MUTATION',
}

export interface ActionSuccessContext {
  type: ProductAdActionType;
  payload?: any;
}

type TreeListProductAd = ProductAds & { __depth: number };
type TreeListCampaignProductAd = CampaignProductAd & { __depth: number };
type TreeListItem = TreeListProductAd | TreeListCampaignProductAd;

interface AutoModeItem {
  key: string;
  level: TreeLevel;
  campaignId?: string;
  adId?: string;
}

interface ResponseBreakdown {
  parentCount: number;
  childCount: number;
  excludedChildCount?: number;
}

interface ErrorDetails {
  successCount: number;
  failureCount: number;
  failedKeys?: string[];
}

export interface BulkAutoModeArgs {
  selectedKeys: (string | number)[];
  selectedItems?: TreeListItem[]; // Optional: only needed for explicit selection mode
  isSelectAll: boolean;
  excludeIds: (string | number)[];
  queryState: State;
  isAutomatic: boolean;
  messages?: {
    success?: string;
    fail?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ProductAdsActionService {
  private loadingStateSubject = new BehaviorSubject<Record<string, boolean>>(
    {},
  );
  public loadingState$ = this.loadingStateSubject.asObservable();

  private readonly isBulkLoadingSubject = new BehaviorSubject<boolean>(false);
  public readonly isBulkLoading$ = this.isBulkLoadingSubject.asObservable();

  private readonly bulkLoadingMessageSubject = new BehaviorSubject<string>('');
  public readonly bulkLoadingMessage$ = this.bulkLoadingMessageSubject.asObservable();

  private readonly actionSuccessSubject = new Subject<ActionSuccessContext>();
  public readonly actionSuccess$ = this.actionSuccessSubject.asObservable();

  // Signal bus for Action Center filters (avoids URL navigation re-init)
  private readonly actionCenterFilterSubject =
    new Subject<CompositeFilterDescriptor | null>();
  public readonly actionCenterFilter$ =
    this.actionCenterFilterSubject.asObservable();

  private isPusherBound = false;
  private bulkLoadingTimeout: any;

  constructor(
    private productAdService: ProductAdsService,
    private modalService: NgbModal,
    private snotifyService: SnotifyService,
    private modalPreferencesService: ModalPreferencesService,
    private pusherService: PusherService,
    private ngZone: NgZone,
  ) {
    this.initPusherListeners();
  }

  private initPusherListeners(): void {
    const userId = this.getUserId();
    if (userId && !this.isPusherBound) {
      this.pusherService.subScribeToChannel(userId, ['notification'], (data) => {
        this.handlePusherNotification(data);
      });
      this.isPusherBound = true;
    }
  }

  private getUserId(): string | null {
    try {
      const userData = sessionStorage.getItem('userInfo');
      if (!userData) return null;
      const user = JSON.parse(userData);
      return user?.userId || null;
    } catch (e) {
      return null;
    }
  }

  private startBulkLoadingTimeout(): void {
    if (this.bulkLoadingTimeout) {
      clearTimeout(this.bulkLoadingTimeout);
    }
    this.bulkLoadingTimeout = setTimeout(() => {
      this.ngZone.run(() => {
        if (this.isBulkLoadingSubject.value) {
          this.isBulkLoadingSubject.next(false);
          this.bulkLoadingMessageSubject.next('');
        }
      });
    }, 10000);
  }

  private clearBulkLoadingTimeout(): void {
    if (this.bulkLoadingTimeout) {
      clearTimeout(this.bulkLoadingTimeout);
      this.bulkLoadingTimeout = null;
    }
  }

  private handlePusherNotification(data: any): void {
    this.ngZone.run(() => {
      if (!environment.production) {
        console.log('ProductAdsActionService: Pusher notification received', data);
      }

      // Handle specific Bulk Ads Status events
      if (data.key === BULK_UPDATE_ADS_STATUS_KEY) {
        if (data.type === 'success' || data.type === 'error') {
          const finalMsg = data.msg || (data.type === 'success' ? 'Bulk update completed.' : 'Bulk update failed.');
          this.clearBulkLoadingTimeout();
          this.isBulkLoadingSubject.next(false);
          this.bulkLoadingMessageSubject.next('');

          if (data.type === 'success') {
            this.snotifyService.success(finalMsg);
            this.actionSuccessSubject.next({ type: ProductAdActionType.STATE_CHANGE });
          } else {
            this.snotifyService.error(finalMsg);
          }

        } else if (data.type === 'info' || data.type === 'notice') {
          // Intermediate status update
          this.bulkLoadingMessageSubject.next(data.msg || 'Processing...');
          if (data.type === 'info') {
            this.snotifyService.info(data.msg || 'Processing...');
          } else {
            // Success notice (like "100/500 processed")
            this.snotifyService.success(data.msg || 'Update received.');
          }
        }
      }
    });
  }


  /**
   * Push a filter from the Action Center to the treelist without URL mutation
   */
  public requestActionCenterView(
    filter: CompositeFilterDescriptor | null,
  ): void {
    this.actionCenterFilterSubject.next(filter);
  }

  /**
   * Called by external flows (e.g., CSV upload) that mutate product ad data
   */
  public notifyExternalMutation(): void {
    this.actionSuccessSubject.next({
      type: ProductAdActionType.EXTERNAL_MUTATION,
    });
  }

  /**
   * Updates the loading state for a specific item and property.
   */
  public setItemLoading(
    id: string | number,
    property: string,
    isLoading: boolean,
  ): void {
    const state = { ...this.loadingStateSubject.value };
    const key = `${id}-${property}`;
    if (isLoading) {
      state[key] = true;
    } else {
      delete state[key];
    }
    this.loadingStateSubject.next(state);
  }

  /**
   * Updates the loading state for multiple items and a specific property.
   * Optimized to only emit once.
   */
  public setItemsLoading(
    ids: (string | number)[],
    property: string,
    isLoading: boolean,
  ): void {
    const state = { ...this.loadingStateSubject.value };
    ids.forEach((id) => {
      const key = `${id}-${property}`;
      if (isLoading) {
        state[key] = true;
      } else {
        delete state[key];
      }
    });
    this.loadingStateSubject.next(state);
  }

  /**
   * Checks if a specific item and property is currently loading.
   */
  public isItemLoading(id: string | number, property: string): boolean {
    return !!this.loadingStateSubject.value[`${id}-${property}`];
  }

  public bulkToggleActiveStatus({
    selectedKeys,
    isSelectAll,
    excludeIds,
    queryState,
    targetState,
    managedScope,
  }: {
    selectedKeys: (string | number)[];
    isSelectAll: boolean;
    excludeIds: (string | number)[];
    queryState: State;
    targetState: ProductAdState;
    managedScope?: ManagedScope;
  }): Observable<boolean> {
    if (this.isBulkLoadingSubject.value) return EMPTY;

    const actionLabel =
      targetState === ProductAdState.ENABLED ? 'Enable' : 'Pause';

    const action$ = isSelectAll
      ? this.productAdService.bulkUpdateProductAdByQuery({
          state: targetState,
          filter: queryState.filter,
          excludeIds: excludeIds as string[],
          managedScope,
        })
      : this.productAdService.updateStateByItemKeys({
          state: targetState,
          keys: selectedKeys as string[],
        });

    this.isBulkLoadingSubject.next(true);
    this.startBulkLoadingTimeout();

    return action$.pipe(
      tap(() => {
        this.isBulkLoadingSubject.next(false);
        this.clearBulkLoadingTimeout();
        this.snotifyService.success(
          `Successfully ${actionLabel.toLowerCase()}d ads.`,
        );
        this.actionSuccessSubject.next({
          type: ProductAdActionType.STATE_CHANGE,
        });
      }),
      map(() => true),
      catchError((err) => {
        this.isBulkLoadingSubject.next(false);
        this.clearBulkLoadingTimeout();
        this.snotifyService.error(
          `Failed to ${actionLabel.toLowerCase()} ads.`,
        );
        return throwError(() => err);
      }),
    );
  }

  public bulkToggleActiveStatusAsync(payload: BulkExecutePayload): Observable<boolean> {
    if (this.isBulkLoadingSubject.value) return EMPTY;

    this.isBulkLoadingSubject.next(true);
    this.bulkLoadingMessageSubject.next('Processing bulk update...');
    this.startBulkLoadingTimeout();

    return this.productAdService.enqueueBulkStateByQuery(payload).pipe(
      tap(({ status }) => {
        if (status === 'completed') {
          this.clearBulkLoadingTimeout();
          this.isBulkLoadingSubject.next(false);
          this.bulkLoadingMessageSubject.next('');
          this.snotifyService.success('Bulk update completed.');
          this.actionSuccessSubject.next({ type: ProductAdActionType.STATE_CHANGE });
        }
        // status === 'queued': loading state stays; Pusher notification will clear it.
      }),
      map(() => true),
      catchError((err) => {
        this.isBulkLoadingSubject.next(false);
        this.bulkLoadingMessageSubject.next('');
        this.clearBulkLoadingTimeout();
        this.snotifyService.error('Failed to initiate bulk update.');
        return throwError(() => err);
      }),
    );
  }


  public bulkToggleAutoMode(args: BulkAutoModeArgs): Observable<boolean> {
    return this.executeBulkAutoMode(args);
  }

  private executeBulkAutoMode(args: BulkAutoModeArgs): Observable<boolean> {
    const { isSelectAll, isAutomatic, messages } = args;
    const actionLabel = isAutomatic ? 'Turn on Auto' : 'Turn off Auto';

    const payload = isSelectAll
      ? this.buildSelectAllPayload(args)
      : this.buildExplicitSelectionPayload(args);

    const defaultSuccessMessage = `Successfully ${actionLabel.toLowerCase()}d for selected ads.`;
    const defaultFailMessage = `Failed to ${actionLabel.toLowerCase()} for selected ads.`;

    return this.productAdService.updateAutoMode(payload).pipe(
      tap((response) =>
        this.notifyAutoModeSuccess(
          response,
          messages?.success || defaultSuccessMessage,
        ),
      ),
      map(() => true),
      catchError((err) => this.handleError(err, messages?.fail || defaultFailMessage)),
    );
  }

  private notifyAutoModeSuccess(
    response: unknown,
    defaultMessage: string,
  ): void {
    const message = this.hasBreakdown(response)
      ? this.buildDetailedSuccessMessage(response.breakdown)
      : defaultMessage;
    this.snotifyService.success(message);
    this.actionSuccessSubject.next({
      type: ProductAdActionType.AUTO_MODE,
      payload: response,
    });
  }

  private buildSelectAllPayload(args: BulkAutoModeArgs) {
    const { isAutomatic, queryState, excludeIds, selectedItems = [] } = args;

    const itemsMap = new Map<string | number, TreeListItem>();
    selectedItems.forEach((item) => {
      itemsMap.set(this.getTreeItemId(item), item);
    });

    return {
      isAutomatic,
      selectAll: {
        filter: queryState.filter,
        excludes: excludeIds.map((id) =>
          this.mapToAutoModeExclude(id, itemsMap),
        ),
      },
    };
  }

  private buildExplicitSelectionPayload(args: BulkAutoModeArgs) {
    const { isAutomatic, selectedItems = [] } = args;
    const eligibleItems = isAutomatic
      ? selectedItems.filter((item) => {
          if (!this.isChildLevel(item)) return true;
          const child = item as TreeListCampaignProductAd;
          const campaignPaused = child.campaignState === 'PAUSED' || child.campaignState === 'ARCHIVED';
          return !campaignPaused && child.managedBySystem === true;
        })
      : selectedItems.filter((item) => {
          if (!this.isChildLevel(item)) return true;
          const child = item as TreeListCampaignProductAd;
          return child.campaignState !== 'PAUSED' && child.campaignState !== 'ARCHIVED';
        });
    return {
      isAutomatic,
      items: eligibleItems.map((item) => this.mapToAutoModeItem(item)),
    };
  }

  private mapToAutoModeExclude(
    excludeId: string | number,
    itemsMap: Map<string | number, TreeListItem>,
  ): AutoModeItem {
    const item = itemsMap.get(excludeId);

    if (!item) {
      return { key: excludeId as string, level: PARENT_LEVEL as TreeLevel };
    }

    return this.mapToAutoModeItem(item);
  }

  private mapToAutoModeItem(item: TreeListItem): AutoModeItem {
    const isChild = this.isChildLevel(item);

    if (isChild) {
      return this.buildChildAutoModeItem(item as TreeListCampaignProductAd);
    }

    return this.buildParentAutoModeItem(item as TreeListProductAd);
  }

  private buildParentAutoModeItem(parent: TreeListProductAd): AutoModeItem {
    return {
      key: parent.itemKey,
      level: PARENT_LEVEL as TreeLevel,
    };
  }

  private buildChildAutoModeItem(
    child: TreeListCampaignProductAd,
  ): AutoModeItem {
    return {
      key: child.adId,
      level: CHILD_LEVEL as TreeLevel,
      campaignId: child.campaignId,
      adId: child.adId,
    };
  }

  private isChildLevel(item: TreeListItem): boolean {
    return item.__depth === CHILD_LEVEL;
  }

  private getTreeItemId(item: TreeListItem): string {
    const parent = item as TreeListProductAd;
    const child = item as TreeListCampaignProductAd;
    return parent.itemKey || child.adId;
  }

  private hasBreakdown(
    response: unknown,
  ): response is { breakdown: ResponseBreakdown } {
    return (
      typeof response === 'object' &&
      response !== null &&
      'breakdown' in response
    );
  }

  private buildDetailedSuccessMessage(breakdown: ResponseBreakdown): string {
    const { parentCount, childCount, excludedChildCount } = breakdown;

    let message = `Updated ${parentCount} parent row${this.pluralize(parentCount)}`;

    if (childCount > 0) {
      message += ` and ${childCount} child row${this.pluralize(childCount)}`;
    }

    if (excludedChildCount && excludedChildCount > 0) {
      message += ` (${excludedChildCount} child row${this.pluralize(excludedChildCount)} excluded)`;
    }

    return message;
  }

  private handleError(err: unknown, defaultMessage: string): Observable<never> {
    const message = this.hasErrorDetails(err)
      ? this.buildDetailedErrorMessage(err.error.details)
      : defaultMessage;

    this.snotifyService.error(message, { timeout: 8000 });
    return throwError(() => err);
  }

  private hasErrorDetails(
    err: unknown,
  ): err is { error: { details: ErrorDetails } } {
    return (
      typeof err === 'object' &&
      err !== null &&
      'error' in err &&
      typeof (err as { error: unknown }).error === 'object' &&
      (err as { error: unknown }).error !== null &&
      'details' in (err as { error: object }).error
    );
  }

  private buildDetailedErrorMessage(details: ErrorDetails): string {
    const { successCount, failureCount, failedKeys } = details;
    const totalAttempted = successCount + failureCount;

    let message = `Updated ${successCount} of ${totalAttempted} items. ${failureCount} item${this.pluralize(failureCount)} failed.`;

    if (failedKeys?.length > 0) {
      message += ` Failed keys: ${failedKeys.join(', ')}`;
    }

    return message;
  }

  private pluralize(count: number): string {
    return count !== 1 ? 's' : '';
  }

  private detectLevel(
    productAd: ProductAds | CampaignProductAd,
    level?: number,
  ): number {
    return level !== undefined
      ? level
      : 'itemKey' in productAd
        ? PARENT_LEVEL
        : CHILD_LEVEL;
  }

  public toggleProductAdAutoMode(
    productAd: ProductAds | CampaignProductAd,
    newValue: boolean,
    level?: number,
    additionalItems: {
      item: ProductAds | CampaignProductAd;
      level: number;
    }[] = [],
  ): Observable<boolean> {
    const actualLevel = this.detectLevel(productAd, level);
    const loadingKey = this.getLoadingKey(productAd, actualLevel);
    const property = 'isAutomatic';

    this.setItemLoading(loadingKey, property, true);
    this.setAdditionalItemsLoading(additionalItems, property, true);

    const items = [
      this.buildAutoModeItemFromLevel(productAd, actualLevel),
      ...additionalItems.map((ai) =>
        this.buildAutoModeItemFromLevel(ai.item, ai.level),
      ),
    ];

    const payload = { isAutomatic: newValue, items };

    return this.productAdService.updateAutoMode(payload).pipe(
      tap((response) =>
        this.notifyAutoModeSuccess(response, 'Successfully updated auto mode.'),
      ),
      map(() => true),
      catchError((err) => {
        this.snotifyService.error('Failed to update auto mode.');
        return throwError(() => err);
      }),
      finalize(() => {
        this.setItemLoading(loadingKey, property, false);
        this.setAdditionalItemsLoading(additionalItems, property, false);
      }),
    );
  }

  private setAdditionalItemsLoading(
    items: { item: ProductAds | CampaignProductAd; level: number }[],
    property: string,
    isLoading: boolean,
  ): void {
    items.forEach((ai) => {
      const key = this.getLoadingKey(ai.item, ai.level);
      this.setItemLoading(key, property, isLoading);
    });
  }

  /**
   * Handles single item status changes with user preferences and change comparison.
   * Supports both parent (level 0) and child (level 1) levels.
   */
  public toggleProductAdStatus(
    productAd: ProductAds | CampaignProductAd,
    newValue: boolean,
    level?: number,
    additionalItems: {
      item: ProductAds | CampaignProductAd;
      level: number;
    }[] = [],
  ): Observable<boolean> {
    const isShowWarningExceedBudget =
      !this.modalPreferencesService.getModalPreference(
        MODAL_PREFERENCES_KEYS.TOGGLE_ACTIVE_STATUS_OF_PRODUCT_AD,
      )?.dontShowAgain;

    const actualLevel = this.detectLevel(productAd, level);
    const state = newValue ? ProductAdState.ENABLED : ProductAdState.PAUSED;
    const loadingKey = this.getLoadingKey(productAd, actualLevel);
    const property = 'active';

    const performUpdate$ = (
      actualLevel === PARENT_LEVEL
        ? this.productAdService.updateStateByItemKeys({
            state,
            keys: [loadingKey],
          })
        : this.productAdService.updateStateByAdIds({
            state,
            adIds: [loadingKey],
          })
    ).pipe(
      tap(() =>
        this.actionSuccessSubject.next({
          type: ProductAdActionType.ACTIVE_STATUS,
        }),
      ),
      map(() => true),
      catchError((err) => {
        this.snotifyService.error('Failed to update product ad state.');
        return throwError(() => err);
      }),
      finalize(() => {
        this.setItemLoading(loadingKey, property, false);
        this.setAdditionalItemsLoading(additionalItems, property, false);
      }),
    );

    this.setItemLoading(loadingKey, property, true);
    this.setAdditionalItemsLoading(additionalItems, property, true);

    if (!isShowWarningExceedBudget) {
      return performUpdate$;
    }

    const modalRef = this.modalService.open(GeneralWarningModalComponent, {
      size: 'md' as string,
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    const sku =
      (productAd as ProductAds).sku ||
      (productAd as CampaignProductAd).campaignName;
    modalRef.componentInstance.title = 'Confirm Changes Status';
    modalRef.componentInstance.subtitle = `You are about to changes the "Active Status" for
        <strong>"${sku}"</strong></strong>.`;

    modalRef.componentInstance.contentComponentData = {
      propertyName: 'Active Status',
      currentValue: !newValue ? 'Enabled' : 'Paused',
      newValue: newValue ? 'Enabled' : 'Paused',
    };

    modalRef.componentInstance.contentComponent = CompareChangesComponent;

    modalRef.componentInstance.setOptions = [
      {
        id: 'liabilityCheckbox',
        desc: `<strong class="text-danger">(*)</strong> I confirm this changes is correct. I understand that
            <strong class="text-danger">we are not responsible</strong> for this update.`,
        required: true,
      },
      {
        id: 'dontShowAgainCheckbox',
        desc: `Don't show this warning again`,
      },
    ];

    return from(modalRef.result).pipe(
      tap(({ formValues: { options } }) => {
        const isDontShowAgain = options.find(
          (option: { id: string; confirm: boolean }) =>
            option.id === 'dontShowAgainCheckbox',
        )?.confirm;

        if (!isDontShowAgain) return;

        this.modalPreferencesService.setDontShowAgain(
          MODAL_PREFERENCES_KEYS.TOGGLE_ACTIVE_STATUS_OF_PRODUCT_AD,
        );
      }),
      switchMap(() => performUpdate$),
      catchError((err) => {
        // Handle modal dismissal or API error cases.
        // Downstream error propagation to trigger UI state reversion.
        return throwError(() => err);
      }),
      finalize(() => {
        this.setItemLoading(loadingKey, property, false);
        this.setAdditionalItemsLoading(additionalItems, property, false);
      }),
    );
  }

  private getLoadingKey(
    productAd: ProductAds | CampaignProductAd,
    level: number,
  ): string {
    return level === PARENT_LEVEL
      ? (productAd as ProductAds).itemKey
      : (productAd as CampaignProductAd).adId;
  }

  private buildAutoModeItemFromLevel(
    productAd: ProductAds | CampaignProductAd,
    level: number,
  ): AutoModeItem {
    if (level === PARENT_LEVEL) {
      return {
        key: (productAd as ProductAds).itemKey,
        level: PARENT_LEVEL as TreeLevel,
      };
    }

    const child = productAd as CampaignProductAd;
    return {
      key: child.adId,
      level: CHILD_LEVEL as TreeLevel,
      campaignId: child.campaignId,
      adId: child.adId,
    };
  }

  public toggleProductAdManaged(
    productAd: CampaignProductAd,
    newManaged: boolean,
  ): Observable<boolean> {
    const adId = productAd.adId;
    const isAutomatic = (productAd as any).isAutomatic === true;
    const loadingKey = adId;
    const property = 'managed';

    const performUpdate$ = this.productAdService.updateManaged({ adId, managed: newManaged }).pipe(
      tap(() => this.actionSuccessSubject.next({ type: ProductAdActionType.MANAGED_FLAG })),
      map(() => true),
      catchError(err => {
        console.error('Failed to update managed flag for ad', adId, err.error);
        this.snotifyService.error(err.error || '');
        return throwError(() => err);
      }),
      finalize(() => this.setItemLoading(loadingKey, property, false)),
    );

    this.setItemLoading(loadingKey, property, true);

    const modalRef = this.modalService.open(GeneralWarningModalComponent, {
      size: 'md' as string,
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    if (newManaged) {
      modalRef.componentInstance.title = 'Enable System Management';
      modalRef.componentInstance.subtitle =
        'This ad will be managed by <strong>Inventory Optimizer</strong>. Once enabled, you can activate <strong>Auto Mode</strong> to automatically pause and re-enable it based on your inventory levels.'
    } else {
      const autoModeWarning = isAutomatic
        ? ' <strong>Auto-mode is ON</strong> — disabling management will also turn off auto-mode for this ad.'
        : '';
      modalRef.componentInstance.title = 'Disable System Management';
      modalRef.componentInstance.subtitle =
        `<strong>Inventory Optimizer</strong> will no longer manage this ad. You will need to pause or enable it manually on Amazon.${autoModeWarning}`;
    }

    modalRef.componentInstance.setOptions = [
      {
        id: 'confirmCheckbox',
        desc: newManaged
          ? '<strong class="text-danger">(*)</strong> I understand Inventory Optimizer will control this ad based on my inventory levels.'
          : '<strong class="text-danger">(*)</strong> I understand I will need to manage this ad manually going forward.',
        required: true,
      },
    ];

    return from(modalRef.result).pipe(
      switchMap(() => performUpdate$),
      catchError(err => {
        this.setItemLoading(loadingKey, property, false);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Opens a confirmation modal and then toggles the managed flag for a parent (ProductAds) row.
   * managed = true  → only ENABLED-campaign children will be set to managed=true (server-side).
   * managed = false → all children are set to managed=false and auto-mode=false.
   */
  public toggleParentProductAdManaged(
    productAd: ProductAds,
    newManaged: boolean,
  ): Observable<boolean> {
    const itemKey = productAd.itemKey;
    const isAutomatic = productAd.isAutomatic === true;
    const loadingKey = itemKey;
    const property = 'managed';

    const performUpdate$ = this.productAdService.updateManagedByItemKey({ itemKey, managed: newManaged }).pipe(
      tap(() => this.actionSuccessSubject.next({ type: ProductAdActionType.MANAGED_FLAG })),
      map(() => true),
      catchError(err => {
        console.error('Failed to update managed flag for item key', itemKey, err.error);
        this.snotifyService.error(err.error || '');
        return throwError(() => err);
      }),
      finalize(() => this.setItemLoading(loadingKey, property, false)),
    );

    this.setItemLoading(loadingKey, property, true);

    const modalRef = this.modalService.open(GeneralWarningModalComponent, {
      size: 'md' as string,
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    if (newManaged) {
      modalRef.componentInstance.title = 'Enable System Management';
      modalRef.componentInstance.subtitle =
        'All ads under this product whose campaigns are currently <strong>active</strong> will be managed by <strong>Inventory Optimizer</strong>. Once enabled, you can activate <strong>Auto Mode</strong> to automatically pause and re-enable them based on inventory levels.';
    } else {
      const autoModeWarning = isAutomatic
        ? ' <strong>Auto-mode is ON</strong> — disabling management will also turn off auto-mode for all ads under this product.'
        : '';
      modalRef.componentInstance.title = 'Disable System Management';
      modalRef.componentInstance.subtitle =
        `<strong>Inventory Optimizer</strong> will no longer manage any ads under this product. You will need to pause or enable them manually on Amazon.${autoModeWarning}`;
    }

    modalRef.componentInstance.setOptions = [
      {
        id: 'confirmCheckbox',
        desc: newManaged
          ? '<strong class="text-danger">(*)</strong> I understand Inventory Optimizer will control these ads based on my inventory levels.'
          : '<strong class="text-danger">(*)</strong> I understand I will need to manage these ads manually going forward.',
        required: true,
      },
    ];

    return from(modalRef.result).pipe(
      switchMap(() => performUpdate$),
      catchError(err => {
        this.setItemLoading(loadingKey, property, false);
        return throwError(() => err);
      }),
    );
  }
}
